// src/pages/portal/PortalEstimates.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { usePortalAuth } from '../../context/PortalAuthContext';
import PortalLayout from '../../components/PortalLayout';

function StatusBadge({ status }) {
  const map = {
    sent:     { bg: '#e8f0ff', color: '#2563eb', label: 'Awaiting Review' },
    approved: { bg: '#e8f7ee', color: '#16a34a', label: 'Approved' },
    declined: { bg: '#fdf0f0', color: '#dc2626', label: 'Declined' },
  };
  const s = map[status?.toLowerCase()] || { bg: '#f0f0ec', color: '#666', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 9px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '600' }}>
      {s.label}
    </span>
  );
}

function fmt(amount) {
  if (amount == null) return '—';
  return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PortalEstimates() {
  const { clientRecord } = usePortalAuth();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientRecord?.id) return;
    // Only show sent/approved/declined estimates to client — never draft
    supabase
      .from('estimates')
      .select('id, title, quote_number, suggested_price, status, created_at, expiry_date, scope_text')
      .eq('client_id', clientRecord.id)
      .in('status', ['sent', 'approved', 'declined'])
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setEstimates(data);
        setLoading(false);
      });
  }, [clientRecord]);

  return (
    <PortalLayout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '600', color: '#1a1a18', margin: 0 }}>Your Estimates</h1>
        <p style={{ fontSize: '13px', color: '#888', margin: '6px 0 0' }}>Review and approve or decline estimates from TLC Unlimited.</p>
      </div>

      {loading ? (
        <div style={{ color: '#aaa', fontSize: '14px' }}>Loading…</div>
      ) : estimates.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>
          No estimates on file yet. Contact TLC Unlimited to get a quote started.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 100px', padding: '10px 18px', background: '#f8f6f2', borderBottom: '1px solid #e8e4dc', fontSize: '11px', color: '#999', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: '600' }}>
            <span>Estimate</span><span>Date</span><span>Amount</span><span>Status</span>
          </div>
          {estimates.map((e, i) => (
            <div key={e.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 110px 110px 100px',
              padding: '14px 18px', borderBottom: i < estimates.length - 1 ? '1px solid #f0ece4' : 'none',
              alignItems: 'center',
            }}>
              <div>
                <Link to={`/portal/estimates/${e.id}`} style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a18', textDecoration: 'none' }}>
                  {e.title || (e.quote_number ? `Quote #${e.quote_number}` : 'Estimate')}
                </Link>
                {e.scope_text && (
                  <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>
                    {e.scope_text.slice(0, 60)}{e.scope_text.length > 60 ? '…' : ''}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '13px', color: '#777' }}>{fmtDate(e.created_at)}</div>
              {/* Use suggested_price — the correct client-facing price field */}
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a18' }}>{fmt(e.suggested_price)}</div>
              <div><StatusBadge status={e.status} /></div>
            </div>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
